import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import {
    PlayCircleFill,
    CheckCircleFill,
    XCircleFill,
    ClockFill,
    ArrowLeft,
    ArrowRight,
    ExclamationTriangleFill,
    PlusCircleFill,
} from 'react-bootstrap-icons';
import apiClient, { API_ENDPOINTS } from '../api/config';
import ScriptArgumentsDialog from '../components/ScriptArgumentsDialog';
import './TestExecutions.css';

/* ============================================================
   Types
   ============================================================ */

interface GenerationArtifact {
    id: number;
    artifact_type: string;
}

interface GenerationJob {
    id: number;
    status: string;
    artifacts: GenerationArtifact[];
}

interface ExecutionItem {
    id: number;
    project_id: number;
    project_name: string;
    artifact_id: number;
    status: string;
    progress: number;
    base_url: string | null;
    run_name: string | null;
    created_at: string;
    completed_at: string | null;
    run_id: string | null;
    total_tests: number | null;
    passed: number | null;
    failed: number | null;
    pass_rate: number | null;
    scope: {
        folder_path?: string[] | null;
        category?: string | null;
        selected_test_count?: number | null;
    } | null;
}

/* ============================================================
   Helpers
   ============================================================ */

function statusBadge(status: string): React.ReactElement {
    switch (status) {
        case 'completed':
            return (
                <Badge bg="" className="exec-badge exec-badge--completed">
                    <CheckCircleFill className="me-1" />Completed
                </Badge>
            );
        case 'running':
            return (
                <Badge bg="" className="exec-badge exec-badge--running">
                    <PlayCircleFill className="me-1" />Running
                </Badge>
            );
        case 'pending':
            return (
                <Badge bg="" className="exec-badge exec-badge--pending">
                    <ClockFill className="me-1" />Pending
                </Badge>
            );
        case 'failed':
            return (
                <Badge bg="" className="exec-badge exec-badge--failed">
                    <XCircleFill className="me-1" />Failed
                </Badge>
            );
        case 'cancelled':
            return (
                <Badge bg="" className="exec-badge exec-badge--cancelled">
                    <ExclamationTriangleFill className="me-1" />Cancelled
                </Badge>
            );
        default:
            return <Badge bg="" className="exec-badge exec-badge--unknown">{status}</Badge>;
    }
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function passRateColor(rate: number | null): string {
    if (rate === null) return 'exec-rate--neutral';
    if (rate >= 80) return 'exec-rate--good';
    if (rate >= 50) return 'exec-rate--warn';
    return 'exec-rate--bad';
}

function scopeLabel(item: ExecutionItem): string {
    if (!item.scope) return 'All tests';
    const { folder_path, category, selected_test_count } = item.scope;
    if (folder_path && folder_path.length > 0) return `Folder: ${folder_path.join(' / ')}`;
    if (category) return `Category: ${category}`;
    if (selected_test_count) return `${selected_test_count} selected`;
    return 'All tests';
}

/* ============================================================
   Component
   ============================================================ */

function TestExecutions(): React.ReactElement {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();

    const [executions, setExecutions] = useState<ExecutionItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showNewRunDialog, setShowNewRunDialog] = useState(false);
    const [newRunArtifactId, setNewRunArtifactId] = useState<number | null>(null);
    const [newRunJobLoading, setNewRunJobLoading] = useState(false);

    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const load = useCallback(async () => {
        if (!projectId) return;
        try {
            setLoading(true);
            setError(null);
            const res = await apiClient.get<ExecutionItem[]>(
                API_ENDPOINTS.PLAYWRIGHT_EXECUTIONS(projectId)
            );
            setExecutions(res.data || []);
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.message || 'Failed to load executions');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    /* ---- Poll while any execution is running/pending ---- */
    useEffect(() => {
        const hasActive = executions.some(
            (ex) => ex.status === 'running' || ex.status === 'pending',
        );
        if (hasActive && !pollingRef.current) {
            pollingRef.current = setInterval(async () => {
                if (!projectId) return;
                try {
                    const res = await apiClient.get<ExecutionItem[]>(
                        API_ENDPOINTS.PLAYWRIGHT_EXECUTIONS(projectId),
                    );
                    setExecutions(res.data || []);
                    const stillActive = (res.data || []).some(
                        (ex) => ex.status === 'running' || ex.status === 'pending',
                    );
                    if (!stillActive && pollingRef.current) {
                        clearInterval(pollingRef.current);
                        pollingRef.current = null;
                    }
                } catch { /* silently ignore poll errors */ }
            }, 8000);
        } else if (!hasActive && pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [executions, projectId]);

    const handleNewRun = useCallback(async () => {
        if (!projectId) return;
        setNewRunJobLoading(true);
        try {
            const res = await apiClient.get<GenerationJob[]>(
                API_ENDPOINTS.PROJECT_REQUIREMENT_GENERATIONS(projectId)
            );
            const jobs: GenerationJob[] = res.data || [];
            let foundArtifactId: number | null = null;
            for (const job of jobs) {
                if (job.status === 'completed') {
                    const art = job.artifacts?.find(a => a.artifact_type === 'test_cases');
                    if (art) {
                        foundArtifactId = art.id;
                        break;
                    }
                }
            }
            setNewRunArtifactId(foundArtifactId);
        } catch {
            setNewRunArtifactId(null);
        } finally {
            setNewRunJobLoading(false);
            setShowNewRunDialog(true);
        }
    }, [projectId]);

    /* ---- Render ---- */

    return (
        <div className="exec-page">
            {/* Page header */}
            <div className="exec-page-header">
                <div className="exec-page-header__left">
                    <button
                        className="exec-back-btn"
                        onClick={() => navigate(`/projects/${projectId}`)}
                        aria-label="Back to project"
                    >
                        <ArrowLeft />
                    </button>
                    <div>
                        <h1 className="exec-page-title">Test Executions</h1>
                        <p className="exec-page-subtitle">
                            All Playwright end-to-end runs for this project
                        </p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button
                        variant="primary"
                        size="sm"
                        className="exec-new-run-btn"
                        onClick={handleNewRun}
                        disabled={newRunJobLoading}
                    >
                        {newRunJobLoading
                            ? <Spinner animation="border" size="sm" className="me-1" />
                            : <PlusCircleFill className="me-1" />}
                        New Run
                    </Button>
                    <button className="exec-refresh-btn" onClick={load} title="Refresh">
                        ↺
                    </button>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="exec-loading">
                    <Spinner animation="border" size="sm" />
                    <span>Loading executions…</span>
                </div>
            ) : error ? (
                <Alert variant="danger" className="exec-error">{error}</Alert>
            ) : executions.length === 0 ? (
                <div className="exec-empty">
                    <PlayCircleFill className="exec-empty-icon" />
                    <p className="exec-empty-title">No executions yet</p>
                    <p className="exec-empty-sub">
                        Run tests from the Test Workspace to see results here.
                    </p>
                </div>
            ) : (
                <div className="exec-table-wrapper">
                    <table className="exec-table" data-responsive-cards="true">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Scope</th>
                                <th className="exec-col-num">Tests</th>
                                <th className="exec-col-num">Passed</th>
                                <th className="exec-col-num">Failed</th>
                                <th className="exec-col-num">Pass Rate</th>
                                <th>Started</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {executions.map((ex) => (
                                <tr key={ex.id} className="exec-row">
                                    <td className="exec-col-name" data-label="Name">
                                        <span className="exec-run-name">
                                            {ex.run_name || <span className="exec-unnamed">Unnamed run</span>}
                                        </span>
                                        {ex.base_url && (
                                            <span className="exec-base-url">{ex.base_url}</span>
                                        )}
                                    </td>
                                    <td data-label="Status">{statusBadge(ex.status)}</td>
                                    <td className="exec-col-scope" data-label="Scope">{scopeLabel(ex)}</td>
                                    <td className="exec-col-num" data-label="Tests">{ex.total_tests ?? '—'}</td>
                                    <td className="exec-col-num exec-passed" data-label="Passed">
                                        {ex.passed ?? '—'}
                                    </td>
                                    <td className="exec-col-num exec-failed" data-label="Failed">
                                        {ex.failed ?? '—'}
                                    </td>
                                    <td className={`exec-col-num exec-rate ${passRateColor(ex.pass_rate)}`} data-label="Pass Rate">
                                        {ex.pass_rate !== null && ex.pass_rate !== undefined
                                            ? `${ex.pass_rate.toFixed(1)}%`
                                            : '—'}
                                    </td>
                                    <td className="exec-col-date" data-label="Started">{formatDate(ex.created_at)}</td>
                                    <td className="exec-col-action" data-label="Actions" data-card-actions="true">
                                        {ex.status === 'completed' && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="exec-detail-btn"
                                                onClick={() =>
                                                    navigate(
                                                        `/projects/${projectId}/executions/${ex.id}`
                                                    )
                                                }
                                            >
                                                Detail <ArrowRight />
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <ScriptArgumentsDialog
                show={showNewRunDialog}
                onHide={() => setShowNewRunDialog(false)}
                projectId={projectId ? Number(projectId) : null}
                artifactId={newRunArtifactId}
                title="New Execution Run"
                onRunStarted={() => { setShowNewRunDialog(false); load(); }}
            />
        </div>
    );
}

export default TestExecutions;
